// src/utils/firstAvailableDate.js — fa-web-7c1d4e9a
//
// SINGLE SOURCE OF TRUTH for the "first available date" capacity walk (web/ESM copy).
// Mirrors src/utils/arrivalWindows.js house style: named exports, pure functions,
// environment differences injected via a `dates` adapter.
//
// ⚠ SERVER MIRROR: functions/utils/firstAvailableDate.js is a hand-synced CommonJS copy
//   (src/ cannot be required from Cloud Functions). Resolver body + adapter shapes MUST
//   stay logic-identical to that file. Change one, change the other, run the gate.
//
// ⚠ BEHAVIOUR FREEZE (Seam 3a): byte-for-byte the three prior copies
//   (JobFormModal.findFirstAvailable, CrmApp.computeFirstAvailableDate, bookNow). Every
//   known bug is PRESERVED and fixed later — do NOT "improve" any here:
//     - raw wt.endH*60 / wt.startH*60 minutes math (no overnight wrap)  — Seam 3b
//     - raw-key window comparison (no resolveArrivalWindowKey)
//     - start-day-only membership (keys off a job's startAt day only)
//     - status-only exclusion (Cancelled/Canceled/Completed; ignores eventType)
//     - UTC-key day membership in the browser adapter (see dateStr note below)
//
// ⚠ THE `dates` ADAPTER bundles the four environment-specific date ops so a caller can
//   never mix browser-local and org-tz math. Pick ONE per call site:
//     browserDates              — JobFormModal, CrmApp (browser tz, local midnight, UTC-ISO key)
//     makeOrgTzDates(tz, today) — bookNow (org tz, noon-UTC base, en-CA key)
//   Seam 3b's Date-based overnight rewrite EXTENDS this adapter; it does not add params.

import { getArrivalWindow } from './arrivalWindows';

// Browser-local adapter. dateStr is UTC-ISO — identical to the prior JobFormModal
// `toDateInput` and CrmApp inline `toDateStr` (both were `date.toISOString().split('T')[0]`).
// NOTE: dateStr keys day membership off a UTC date while addDays/dayName walk LOCAL days —
// a job at 8:00 PM in America/Toronto (00:00 UTC next day) is filed on the wrong day.
// Pre-existing in both web copies; preserved here, fixed in a later seam.
export const browserDates = {
  startOfToday: () => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; },
  addDays: (base, i) => { const c = new Date(base); c.setDate(base.getDate() + i); return c; },
  dayName: (d) => ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][d.getDay()],
  dateStr: (d) => d.toISOString().split('T')[0],
  // A wall-clock instant (h:m) on d's LOCAL calendar day. DST handled by the JS Date ctor.
  atTime: (d, h, m) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m, 0, 0),
};

// Org-timezone adapter. Caller pins `today` (noon-UTC, DST-safe) and passes the org
// timezone; dayName/dateStr resolve in that zone — identical to the prior getDayName /
// toLocalDateStr helpers.
export const makeOrgTzDates = (timeZone, today) => ({
  startOfToday: () => today,
  addDays: (base, i) => { const c = new Date(base); c.setUTCDate(base.getUTCDate() + i); return c; },
  dayName: (d) => {
    const date = d.toDate ? d.toDate() : d;
    return date.toLocaleDateString('en-US', { timeZone, weekday: 'long' }).toLowerCase();
  },
  dateStr: (d) => {
    const date = d.toDate ? d.toDate() : d;
    return date.toLocaleDateString('en-CA', { timeZone });
  },
  // A wall-clock instant (h:m) on d's ORG-timezone calendar day. DST-aware: the offset is
  // probed for d's own day, so a wrapped window's end (passed addDays(check,1)) uses the
  // end-day offset. Mirrors bookNow's start-time offset technique (index.js ~12828).
  atTime: (d, h, m) => {
    const date = d.toDate ? d.toDate() : d;
    const ds = date.toLocaleDateString('en-CA', { timeZone });
    const probe = new Date(`${ds}T12:00:00Z`);
    const tzHour = parseInt(probe.toLocaleString('en-US', { timeZone, hour12: false, hour: '2-digit' }), 10) % 24;
    const utcOffset = probe.getUTCHours() - tzHour;
    const out = new Date(`${ds}T00:00:00Z`);
    out.setUTCHours(h + utcOffset, m, 0, 0);
    return out;
  },
});

// First Date whose selected window has room, else the first work day within 14 days, else
// null. The caller applies its own terminal fallback (JobFormModal: none; CrmApp: tomorrow;
// bookNow: today+1).
export const resolveFirstAvailableDate = ({
  jobs, arrivalWindow, durationMins, travelBuffer, workDays, dates, companySettings, // aw-seam4
}) => {
  const wt = getArrivalWindow(arrivalWindow, companySettings); // aw-seam4
  const startMins = wt.startH * 60 + wt.startM;
  const endMins   = wt.endH * 60 + wt.endM;
  const wraps     = endMins <= startMins;                                   // overnight window
  const bufferedMs = (travelBuffer + durationMins + travelBuffer) * 60000;  // double buffer — frozen

  const today = dates.startOfToday();

  for (let i = 1; i <= 90; i++) {
    const check = dates.addDays(today, i);
    if (!workDays.includes(dates.dayName(check))) continue;
    const checkStr = dates.dateStr(check);

    const dayJobs = (jobs || []).filter(j => {
      if (!j.startAt) return false;
      if (['Cancelled', 'Canceled', 'Completed'].includes(j.status)) return false;
      const jStart = j.startAt?.toDate ? j.startAt.toDate() : new Date(j.startAt);
      if (dates.dateStr(jStart) !== checkStr) return false;
      const jw = j.arrivalWindow || 'morning';
      return jw === arrivalWindow || jw === 'allday' || arrivalWindow === 'allday';
    });

    // Window as real instants on the check day; end rolls to the next day when it wraps.
    const windowStart = dates.atTime(check, wt.startH, wt.startM);
    const windowEnd   = wraps
      ? dates.atTime(dates.addDays(check, 1), wt.endH, wt.endM)
      : dates.atTime(check, wt.endH, wt.endM);

    let latestEnd = windowStart.getTime();                                  // seed = window start (frozen)
    dayJobs.forEach(j => {
      const jEnd = j.endAt?.toDate ? j.endAt.toDate() : (j.endAt instanceof Date ? j.endAt : null);
      if (jEnd && jEnd.getTime() > latestEnd) latestEnd = jEnd.getTime();   // latest end wins (frozen)
    });

    if (latestEnd + bufferedMs <= windowEnd.getTime()) {
      return check;
    }
  }

  for (let i = 1; i <= 14; i++) {
    const check = dates.addDays(today, i);
    if (workDays.includes(dates.dayName(check))) return check;
  }

  return null;
};
